// src/pages/lms/LMSLibrary.jsx — Enterprise Library System
// Tabs: Students | Books Catalog | Active Borrows | History | Settings
// Typing lag fixed: all form modals are isolated components
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import sendNotification from '../../services/NotificationService';
import { useLMSAuth } from '../../hooks/useLMSAuth';
import LMSLayout from './LMSLayout';
import LMSPageGuard from '../../components/lms/LMSPageGuard';
import { Plus, X, Search, Trash2, BookOpen, Users, RefreshCw,
         CheckCircle, AlertCircle, Upload, Library } from 'lucide-react';

const CATEGORIES = ['Fiction','Non-Fiction','Science','Mathematics','History',
  'Literature','Technology','Religion','Social Sciences','Reference','Magazine','Other'];
const LANGUAGES  = ['English','Urdu','Arabic','Other'];
const INP = {
  width:'100%', padding:'9px 12px', borderRadius:8,
  border:'1.5px solid #E5E7EB', fontSize:13,
  boxSizing:'border-box', outline:'none', color:'#111827',
};

// ── Book Form Modal ────────────────────────────────────
function BookFormModal({ editBook, institutionId, account, onSave, onClose }) {
  const [saving,    setSaving]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const [coverUrl,  setCoverUrl]  = useState(editBook?.cover_image_url||'');
  const [category,  setCategory]  = useState(editBook?.category||'');
  const [language,  setLanguage]  = useState(editBook?.language||'English');
  // Text inputs — uncontrolled refs
  const titleRef  = useRef(); const authorRef   = useRef();
  const isbnRef   = useRef(); const publisherRef = useRef();
  const yearRef   = useRef(); const copiesRef    = useRef();
  const shelfRef  = useRef(); const descRef      = useRef();

  const uploadCover = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const path = `${institutionId}/books/${Date.now()}_${file.name}`;
      await supabase.storage.from('lms-library').upload(path, file, { upsert: true });
      const { data:{ publicUrl } } = supabase.storage.from('lms-library').getPublicUrl(path);
      setCoverUrl(publicUrl);
    } catch(e) { setError('Cover upload failed: '+e.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const title = titleRef.current?.value?.trim()||'';
    if (!title) { setError('Book title is required'); return; }
    setSaving(true); setError('');
    try {
      const copies = Number(copiesRef.current?.value)||1;
      const data = {
        institution_id:  institutionId,
        title,
        author:          authorRef.current?.value?.trim()||null,
        isbn:            isbnRef.current?.value?.trim()||null,
        publisher:       publisherRef.current?.value?.trim()||null,
        published_year:  yearRef.current?.value ? Number(yearRef.current.value) : null,
        category:        category||null,
        language:        language||'English',
        description:     descRef.current?.value?.trim()||null,
        cover_image_url: coverUrl||null,
        total_copies:    copies,
        available_copies:editBook ? undefined : copies,
        shelf_location:  shelfRef.current?.value?.trim()||null,
        added_by:        account?.lms_username||'Admin',
        is_active:       true,
      };
      if (!editBook) {
        const { error:e } = await supabase.from('lms_books').insert(data);
        if (e) throw e;
      } else {
        delete data.available_copies;
        const { error:e } = await supabase.from('lms_books').update(data).eq('id', editBook.id);
        if (e) throw e;
      }
      onSave(editBook ? 'Book updated!' : 'Book added to catalog!');
    } catch(e) { setError(e.message||'Save failed'); console.error(e); }
    finally { setSaving(false); }
  };

  const F = ({label, children, col}) => (
    <div style={{gridColumn:col||'span 1'}}>
      <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>{label}</label>
      {children}
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:680,maxHeight:'92vh',overflowY:'auto'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1}}>
          <h3 style={{margin:0,fontWeight:800,color:'#111827',fontSize:18}}>{editBook?'Edit Book':'Add Book to Catalog'}</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={22} color="#6B7280"/></button>
        </div>
        <div style={{padding:24}}>
          {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:13,color:'#991B1B',fontWeight:600}}>❌ {error}</div>}

          {/* Cover image */}
          <div style={{display:'flex',gap:16,marginBottom:20,padding:16,background:'#F9FAFB',borderRadius:12,alignItems:'center'}}>
            <div style={{width:80,height:110,borderRadius:10,overflow:'hidden',flexShrink:0,border:'2px solid #E5E7EB',background:'#F3F4F6',display:'flex',alignItems:'center',justifyContent:'center'}}>
              {coverUrl
                ? <img src={coverUrl} alt="cover" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                : <BookOpen size={28} color="#D1D5DB"/>}
            </div>
            <div>
              <div style={{fontWeight:700,color:'#111827',fontSize:13,marginBottom:6}}>Book Cover Image</div>
              <label style={{cursor:'pointer'}}>
                <input type="file" accept="image/*" style={{display:'none'}}
                  onChange={e=>e.target.files?.[0]&&uploadCover(e.target.files[0])}/>
                <span style={{fontSize:12,color:'#4F8EF7',fontWeight:600,padding:'6px 14px',border:'1px solid #BFDBFE',borderRadius:6,background:'#EFF6FF'}}>
                  {uploading?'Uploading...':'📷 Upload Cover'}
                </span>
              </label>
              {coverUrl && <div style={{marginTop:6}}><button onClick={()=>setCoverUrl('')} style={{fontSize:11,color:'#EF4444',background:'none',border:'none',cursor:'pointer'}}>✕ Remove</button></div>}
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <F label="Book Title *" col="span 2"><input ref={titleRef} defaultValue={editBook?.title||''} placeholder="e.g. Introduction to Computer Science" style={INP}/></F>
            <F label="Author"><input ref={authorRef} defaultValue={editBook?.author||''} placeholder="Author name" style={INP}/></F>
            <F label="ISBN"><input ref={isbnRef} defaultValue={editBook?.isbn||''} placeholder="ISBN number" style={INP}/></F>
            <F label="Publisher"><input ref={publisherRef} defaultValue={editBook?.publisher||''} placeholder="Publisher name" style={INP}/></F>
            <F label="Published Year"><input ref={yearRef} type="number" defaultValue={editBook?.published_year||''} placeholder="e.g. 2023" style={INP}/></F>
            <F label="Category">
              <select value={category} onChange={e=>setCategory(e.target.value)} style={INP}>
                <option value="">Select category...</option>
                {CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </F>
            <F label="Language">
              <select value={language} onChange={e=>setLanguage(e.target.value)} style={INP}>
                {LANGUAGES.map(l=><option key={l} value={l}>{l}</option>)}
              </select>
            </F>
            <F label="Total Copies"><input ref={copiesRef} type="number" min="1" defaultValue={editBook?.total_copies||1} style={INP}/></F>
            <F label="Shelf Location"><input ref={shelfRef} defaultValue={editBook?.shelf_location||''} placeholder="e.g. Shelf A-3, Row 2" style={INP}/></F>
            <F label="Description" col="span 2"><textarea ref={descRef} defaultValue={editBook?.description||''} rows={2} placeholder="Brief description..." style={{...INP,resize:'vertical'}}/></F>
          </div>

          <div style={{display:'flex',gap:10,marginTop:20}}>
            <button onClick={onClose} style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer',fontSize:13}}>Cancel</button>
            <button onClick={save} disabled={saving} style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:saving?'#9CA3AF':'#fff',fontWeight:700,cursor:saving?'wait':'pointer',fontSize:14}}>
              {saving?'Saving...':editBook?'Update Book':'Add Book'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Issue Book Modal ───────────────────────────────────
function IssueBookModal({ student, books, institutionId, account, onSave, onClose }) {
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [selBook,  setSelBook]  = useState('');
  const [finePerDay, setFinePerDay] = useState('10');
  const dueDateRef = useRef();
  const notesRef   = useRef();

  // Default due date = 14 days from now
  const defaultDue = new Date();
  defaultDue.setDate(defaultDue.getDate() + 14);
  const defaultDueStr = defaultDue.toISOString().slice(0,16);

  const selectedBook = books.find(b=>b.id===selBook);

  const issue = async () => {
    if (!selBook)            { setError('Select a book');      return; }
    if (!dueDateRef.current?.value) { setError('Set a due date'); return; }
    if ((selectedBook?.available_copies||0) < 1) { setError('No copies available for this book'); return; }
    setSaving(true); setError('');
    try {
      const { error:e } = await supabase.from('lms_borrow_records').insert({
        institution_id:       institutionId,
        book_id:              selBook,
        book_title:           selectedBook.title,
        book_author:          selectedBook.author||null,
        book_cover_url:       selectedBook.cover_image_url||null,
        student_id:           student.id||null,
        student_name:         student.full_name,
        student_roll_number:  student.roll_no||null,
        student_username:     student.username||null,
        student_class:        student.class_name||null,
        student_phone:        student.phone||null,
        issued_at:            new Date().toISOString(),
        due_date:             new Date(dueDateRef.current.value).toISOString(),
        status:               'borrowed',
        fine_per_day:         Number(finePerDay)||10,
        issued_by:            account?.lms_username||'Admin',
        notes:                notesRef.current?.value?.trim()||null,
      });
      if (e) throw e;

      await sendNotification({
        institution_id: institutionId,
        recipient_ids: [student.id],
        title: '📚 Book Issued to You',
        body: `"${selectedBook.title}" — due back by ${new Date(dueDateRef.current.value).toLocaleDateString()}`,
        notification_type: 'library',
      });
      onSave(`✅ "${selectedBook.title}" issued to ${student.full_name}`);
    } catch(e) { setError(e.message||'Issue failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:'#fff',borderRadius:20,width:'100%',maxWidth:500,maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid #E5E7EB',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,background:'#fff',zIndex:1}}>
          <h3 style={{margin:0,fontWeight:800,color:'#111827',fontSize:17}}>Issue Book</h3>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer'}}><X size={20} color="#6B7280"/></button>
        </div>
        <div style={{padding:24}}>
          {error && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:8,padding:'10px',marginBottom:14,fontSize:13,color:'#991B1B',fontWeight:600}}>❌ {error}</div>}

          {/* Student info */}
          <div style={{background:'linear-gradient(135deg,#EFF6FF,#F5F3FF)',border:'1px solid #BFDBFE',borderRadius:12,padding:14,marginBottom:18}}>
            <div style={{fontSize:11,color:'#6B7280',fontWeight:700,marginBottom:4}}>ISSUING TO</div>
            <div style={{fontWeight:800,color:'#111827',fontSize:15}}>{student.full_name}</div>
            <div style={{fontSize:12,color:'#6B7280',marginTop:2,display:'flex',gap:10,flexWrap:'wrap'}}>
              {student.roll_no && <span>Roll# {student.roll_no}</span>}
              {student.degree  && <span>{student.degree}{student.year_of_study?` · Yr ${student.year_of_study}`:''}</span>}
              {student.class_name && <span>Class: {student.class_name}</span>}
            </div>
          </div>

          {/* Book selector */}
          <div style={{marginBottom:14}}>
            <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Select Book *</label>
            <select value={selBook} onChange={e=>setSelBook(e.target.value)} style={INP}>
              <option value="">— Choose a book —</option>
              {books.filter(b=>b.available_copies>0).map(b=>(
                <option key={b.id} value={b.id}>{b.title}{b.author?` — ${b.author}`:''} ({b.available_copies} available)</option>
              ))}
            </select>
            {selBook && selectedBook && (
              <div style={{marginTop:8,display:'flex',gap:10,alignItems:'center',padding:'8px 10px',background:'#F0FDF4',borderRadius:8,border:'1px solid #BBF7D0'}}>
                {selectedBook.cover_image_url&&<img src={selectedBook.cover_image_url} alt="" style={{width:32,height:44,objectFit:'cover',borderRadius:4}}/>}
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:'#065F46'}}>{selectedBook.title}</div>
                  {selectedBook.shelf_location&&<div style={{fontSize:11,color:'#6B7280'}}>📍 {selectedBook.shelf_location}</div>}
                </div>
              </div>
            )}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
            <div>
              <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Due Date & Time *</label>
              <input ref={dueDateRef} type="datetime-local" defaultValue={defaultDueStr} style={INP}/>
            </div>
            <div>
              <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Fine Per Day (Rs)</label>
              <input type="number" min="0" value={finePerDay} onChange={e=>setFinePerDay(e.target.value)} style={INP}/>
            </div>
          </div>

          <div style={{marginBottom:18}}>
            <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:5}}>Notes (optional)</label>
            <input ref={notesRef} defaultValue="" placeholder="Any notes about this issue..." style={INP}/>
          </div>

          <div style={{display:'flex',gap:10}}>
            <button onClick={onClose} style={{flex:1,padding:'12px 0',borderRadius:10,border:'1.5px solid #E5E7EB',background:'#fff',color:'#374151',fontWeight:600,cursor:'pointer'}}>Cancel</button>
            <button onClick={issue} disabled={saving} style={{flex:2,padding:'12px 0',borderRadius:10,border:'none',background:saving?'#E5E7EB':'linear-gradient(135deg,#10B981,#059669)',color:saving?'#9CA3AF':'#fff',fontWeight:700,cursor:saving?'wait':'pointer',fontSize:14}}>
              {saving?'Issuing...':'📚 Issue Book'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────
export default function LMSLibrary() {
  const { account, institution } = useLMSAuth();
  const [tab,      setTab]      = useState('students');
  const [students, setStudents] = useState([]);
  const [books,    setBooks]    = useState([]);
  const [borrows,  setBorrows]  = useState([]);
  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [success,  setSuccess]  = useState('');
  const [error,    setError]    = useState('');
  // Modals
  const [showBookForm,  setShowBookForm]  = useState(false);
  const [showIssue,     setShowIssue]     = useState(false);
  const [editBook,      setEditBook]      = useState(null);
  const [issueStudent,  setIssueStudent]  = useState(null);
  // Debounced searches
  const [studInput,  setStudInput]  = useState('');
  const [studSearch, setStudSearch] = useState('');
  const [bookInput,  setBookInput]  = useState('');
  const [bookSearch, setBookSearch] = useState('');
  const [borInput,   setBorInput]   = useState('');
  const [borSearch,  setBorSearch]  = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const sTimer = useRef(); const bTimer = useRef(); const brTimer = useRef();
  const hs = (v,setI,setS,t) => { setI(v); clearTimeout(t.current); t.current=setTimeout(()=>setS(v),200); };

  useEffect(()=>{ if(institution?.id) loadAll(); },[institution?.id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const id = institution.id;
      // Mark overdue first
      try { await supabase.rpc('mark_overdue_borrows'); } catch(_){}

      const [stuRes, bookRes, borRes, statRes] = await Promise.all([
        supabase.from('profiles').select('id,full_name,username,roll_no,degree,year_of_study,phone,email')
          .eq('institution_id',id).eq('role','student').order('full_name'),
        supabase.from('lms_books').select('*').eq('institution_id',id).eq('is_active',true).order('title'),
        supabase.from('lms_borrow_records').select('*').eq('institution_id',id).order('issued_at',{ascending:false}),
        supabase.rpc('get_library_stats',{p_institution_id:id}).then(r=>r.data).catch(()=>null),
      ]);

      // Attach class info to students
      const clsRes = await supabase.from('lms_class_students')
        .select('student_id,student_roll_number,class_id,lms_classes(name)')
        .eq('institution_id',id).eq('enrollment_status','active');
      const clsMap = {};
      for (const c of (clsRes.data||[])) {
        if (c.student_id) clsMap[c.student_id] = c.lms_classes?.name||'';
        if (c.student_roll_number) clsMap[c.student_roll_number] = c.lms_classes?.name||'';
      }

      const studs = (stuRes.data||[]).map(s => ({
        ...s,
        class_name: clsMap[s.id] || clsMap[s.roll_no] || '',
        activeBorrows: (borRes.data||[]).filter(b=>
          (b.student_id===s.id||(s.roll_no&&b.student_roll_number===s.roll_no))&&
          b.status!=='returned').length,
        overdueBorrows: (borRes.data||[]).filter(b=>
          (b.student_id===s.id||(s.roll_no&&b.student_roll_number===s.roll_no))&&
          b.status==='overdue').length,
      }));

      setStudents(studs);
      setBooks(bookRes.data||[]);
      setBorrows(borRes.data||[]);
      setStats(statRes);
    } catch(e) { console.error('Library load error:',e); setError('Load failed: '+e.message); }
    finally { setLoading(false); }
  };

  const handleReturn = async (borrow) => {
    if (!window.confirm(`Mark "${borrow.book_title}" as returned by ${borrow.student_name}?`)) return;
    try {
      await supabase.from('lms_borrow_records').update({
        status:      'returned',
        returned_at: new Date().toISOString(),
        fine_amount: borrow.status==='overdue' ?
          Math.ceil((new Date()-new Date(borrow.due_date))/(1000*60*60*24)) * (borrow.fine_per_day||10) : 0,
      }).eq('id', borrow.id);
      setSuccess(`✅ "${borrow.book_title}" returned by ${borrow.student_name}`);
      setTimeout(()=>setSuccess(''),4000);
      await loadAll();
    } catch(e) { setError(e.message); }
  };

  const handleMarkFinePaid = async (id) => {
    await supabase.from('lms_borrow_records').update({fine_paid:true,fine_paid_at:new Date().toISOString()}).eq('id',id);
    setSuccess('✅ Fine marked as paid');
    setTimeout(()=>setSuccess(''),3000);
    await loadAll();
  };

  const handleDeleteBook = async (id) => {
    if (!window.confirm('Remove this book from catalog?')) return;
    await supabase.from('lms_books').update({is_active:false}).eq('id',id);
    setSuccess('Book removed from catalog');
    setTimeout(()=>setSuccess(''),3000);
    await loadAll();
  };

  const onSaved = async (msg) => {
    setSuccess(msg); setShowBookForm(false); setShowIssue(false);
    setEditBook(null); setIssueStudent(null);
    setTimeout(()=>setSuccess(''),4000);
    await loadAll();
  };

  // Filtered lists
  const filtStudents = useMemo(()=>students.filter(s=>{
    if (!studSearch.trim()) return true;
    const q=studSearch.toLowerCase();
    return (s.full_name||'').toLowerCase().includes(q)||
           (s.roll_no||'').toLowerCase().includes(q)||
           (s.username||'').toLowerCase().includes(q)||
           (s.class_name||'').toLowerCase().includes(q);
  }),[students,studSearch]);

  const filtBooks = useMemo(()=>books.filter(b=>{
    if (!bookSearch.trim()) return true;
    const q=bookSearch.toLowerCase();
    return (b.title||'').toLowerCase().includes(q)||
           (b.author||'').toLowerCase().includes(q)||
           (b.isbn||'').toLowerCase().includes(q)||
           (b.category||'').toLowerCase().includes(q);
  }),[books,bookSearch]);

  const filtBorrows = useMemo(()=>borrows.filter(b=>{
    const mSt = !statusFilter||b.status===statusFilter;
    if (!mSt) return false;
    if (!borSearch.trim()) return true;
    const q=borSearch.toLowerCase();
    return (b.student_name||'').toLowerCase().includes(q)||
           (b.book_title||'').toLowerCase().includes(q)||
           (b.student_roll_number||'').toLowerCase().includes(q);
  }),[borrows,borSearch,statusFilter]);

  const tabs = [
    {k:'students', l:'👥 Students'},
    {k:'catalog',  l:'📚 Catalog'},
    {k:'active',   l:'📖 Active Borrows'},
    {k:'history',  l:'📋 History'},
  ];

  const statusColors = {
    borrowed: {bg:'#EFF6FF',color:'#1D4ED8',border:'#BFDBFE'},
    overdue:  {bg:'#FEF2F2',color:'#991B1B',border:'#FECACA'},
    returned: {bg:'#F0FDF4',color:'#065F46',border:'#BBF7D0'},
    lost:     {bg:'#FFF7ED',color:'#92400E',border:'#FED7AA'},
  };

  return (
    <LMSLayout>
      <LMSPageGuard pageKey="library">

        {/* Header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:'#111827',margin:0}}>Library</h1>
            <p style={{color:'#6B7280',fontSize:13,margin:'4px 0 0'}}>
              {stats?.total_books||0} books · {stats?.borrowed_count||0} borrowed · {stats?.overdue_count||0} overdue
            </p>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={loadAll} style={{display:'flex',alignItems:'center',gap:6,padding:'9px 14px',borderRadius:9,border:'1px solid #E5E7EB',background:'#fff',color:'#374151',fontSize:13,fontWeight:600,cursor:'pointer'}}>
              <RefreshCw size={14}/> Refresh
            </button>
            <button onClick={()=>{setEditBook(null);setShowBookForm(true);}}
              style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',borderRadius:10,border:'none',background:'linear-gradient(135deg,#4F8EF7,#7C3AED)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer'}}>
              <Plus size={16}/> Add Book
            </button>
          </div>
        </div>

        {success && <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#065F46',fontWeight:700}}>{success}</div>}
        {error   && <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:10,padding:'12px 16px',marginBottom:16,fontSize:13,color:'#991B1B'}}>{error}<button onClick={()=>setError('')} style={{float:'right',background:'none',border:'none',cursor:'pointer'}}>✕</button></div>}

        {/* Stats */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:12,marginBottom:20}}>
          {[
            {l:'Total Books',  v:stats?.total_books||0,      c:'#4F8EF7', e:'📚'},
            {l:'Available',    v:stats?.available_copies||0,  c:'#10B981', e:'✅'},
            {l:'Borrowed',     v:stats?.borrowed_count||0,    c:'#3B82F6', e:'📖'},
            {l:'Overdue',      v:stats?.overdue_count||0,     c:'#EF4444', e:'⚠️'},
            {l:'Total Fines',  v:`Rs ${Number(stats?.total_fines||0).toLocaleString()}`, c:'#F59E0B', e:'💰'},
            {l:'Unpaid Fines', v:`Rs ${Number(stats?.unpaid_fines||0).toLocaleString()}`, c:'#DC2626', e:'❗'},
          ].map(s=>(
            <div key={s.l} style={{background:'#fff',borderRadius:12,border:'1px solid #E5E7EB',padding:'14px 16px'}}>
              <div style={{fontSize:20,marginBottom:4}}>{s.e}</div>
              <div style={{fontSize:18,fontWeight:900,color:s.c}}>{s.v}</div>
              <div style={{fontSize:11,color:'#6B7280'}}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{display:'flex',background:'#F1F5F9',borderRadius:12,padding:4,marginBottom:20,gap:2,flexWrap:'wrap'}}>
          {tabs.map(t=>(
            <button key={t.k} onClick={()=>setTab(t.k)}
              style={{padding:'8px 16px',borderRadius:9,border:'none',background:tab===t.k?'#fff':'transparent',color:tab===t.k?'#1E293B':'#64748B',fontWeight:700,fontSize:13,cursor:'pointer',boxShadow:tab===t.k?'0 1px 4px rgba(0,0,0,0.08)':'none'}}>
              {t.l}
            </button>
          ))}
        </div>

        {/* ── STUDENTS TAB ── */}
        {tab==='students' && (
          <>
            <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:12,marginBottom:14,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
              <div style={{position:'relative',flex:1,minWidth:200}}>
                <Search size={14} color="#9CA3AF" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
                <input value={studInput} onChange={e=>hs(e.target.value,setStudInput,setStudSearch,sTimer)}
                  placeholder="Search by name, roll no, class, username..." style={{...INP,paddingLeft:28}}/>
              </div>
              <span style={{fontSize:12,color:'#6B7280',fontWeight:600}}>{filtStudents.length}/{students.length} students</span>
            </div>
            {loading ? <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading...</div> :
            <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'auto'}}>
              {filtStudents.length===0
                ? <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>No students found</div>
                : <table style={{width:'100%',borderCollapse:'collapse',minWidth:800}}>
                    <thead>
                      <tr style={{background:'#F8FAFF',borderBottom:'1px solid #E5E7EB'}}>
                        {['#','Student','Roll No','Class','Degree','Active Borrows','Overdue','Actions'].map(h=>(
                          <th key={h} style={{padding:'11px 14px',textAlign:'left',fontSize:11,fontWeight:800,color:'#6B7280',whiteSpace:'nowrap'}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtStudents.map((s,i)=>(
                        <tr key={s.id} style={{borderBottom:i<filtStudents.length-1?'1px solid #F1F5F9':'none',background:s.overdueBorrows>0?'#FFF5F5':'transparent'}}>
                          <td style={{padding:'10px 14px',fontSize:11,color:'#9CA3AF',fontWeight:600}}>{i+1}</td>
                          <td style={{padding:'10px 14px'}}>
                            <div style={{fontWeight:700,color:'#111827',fontSize:13}}>{s.full_name}</div>
                            {s.username&&<div style={{fontSize:11,color:'#9CA3AF'}}>@{s.username}</div>}
                          </td>
                          <td style={{padding:'10px 14px'}}>
                            {s.roll_no
                              ? <code style={{fontSize:11,fontWeight:800,color:'#4F8EF7',background:'#EFF6FF',padding:'2px 7px',borderRadius:4}}>{s.roll_no}</code>
                              : <span style={{color:'#D1D5DB',fontSize:11}}>—</span>}
                          </td>
                          <td style={{padding:'10px 14px',fontSize:12,color:'#374151'}}>{s.class_name||'—'}</td>
                          <td style={{padding:'10px 14px',fontSize:12,color:'#374151'}}>{s.degree||'—'}{s.year_of_study?` Yr${s.year_of_study}`:''}</td>
                          <td style={{padding:'10px 14px'}}>
                            <span style={{fontSize:13,fontWeight:900,color:s.activeBorrows>0?'#3B82F6':'#9CA3AF'}}>{s.activeBorrows}</span>
                          </td>
                          <td style={{padding:'10px 14px'}}>
                            <span style={{fontSize:13,fontWeight:900,color:s.overdueBorrows>0?'#EF4444':'#9CA3AF'}}>{s.overdueBorrows}</span>
                          </td>
                          <td style={{padding:'8px 12px'}}>
                            <button onClick={()=>{setIssueStudent(s);setShowIssue(true);}}
                              style={{padding:'6px 12px',borderRadius:7,border:'none',background:'linear-gradient(135deg,#10B981,#059669)',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer'}}>
                              📚 Issue Book
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>}
            </div>}
          </>
        )}

        {/* ── CATALOG TAB ── */}
        {tab==='catalog' && (
          <>
            <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:12,marginBottom:14,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
              <div style={{position:'relative',flex:1,minWidth:200}}>
                <Search size={14} color="#9CA3AF" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
                <input value={bookInput} onChange={e=>hs(e.target.value,setBookInput,setBookSearch,bTimer)}
                  placeholder="Search by title, author, ISBN, category..." style={{...INP,paddingLeft:28}}/>
              </div>
              <span style={{fontSize:12,color:'#6B7280',fontWeight:600}}>{filtBooks.length}/{books.length} books</span>
            </div>
            {loading ? <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading...</div> :
            filtBooks.length===0
              ? <div style={{padding:60,textAlign:'center',background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',color:'#9CA3AF'}}>No books in catalog yet. Add your first book!</div>
              : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:14}}>
                  {filtBooks.map(b=>{
                    const avail = b.available_copies>0;
                    return (
                      <div key={b.id} style={{background:'#fff',borderRadius:14,border:`1.5px solid ${avail?'#E5E7EB':'#FECACA'}`,overflow:'hidden'}}>
                        {/* Cover */}
                        <div style={{height:120,background:'linear-gradient(135deg,#EFF6FF,#F5F3FF)',display:'flex',alignItems:'center',justifyContent:'center',position:'relative',overflow:'hidden'}}>
                          {b.cover_image_url
                            ? <img src={b.cover_image_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                            : <div style={{textAlign:'center'}}>
                                <BookOpen size={36} color="#BFDBFE"/>
                                <div style={{fontSize:10,color:'#9CA3AF',marginTop:4}}>{b.category||'Book'}</div>
                              </div>}
                          <span style={{position:'absolute',top:8,right:8,fontSize:10,fontWeight:800,color:avail?'#059669':'#EF4444',background:avail?'#F0FDF4':'#FEF2F2',padding:'3px 8px',borderRadius:5,border:`1px solid ${avail?'#BBF7D0':'#FECACA'}`}}>
                            {b.available_copies}/{b.total_copies}
                          </span>
                        </div>
                        {/* Info */}
                        <div style={{padding:'12px 14px'}}>
                          <div style={{fontWeight:800,color:'#111827',fontSize:14,marginBottom:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.title}</div>
                          {b.author&&<div style={{fontSize:12,color:'#6B7280',marginBottom:4}}>✍️ {b.author}</div>}
                          <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:8}}>
                            {b.category&&<span style={{fontSize:10,color:'#7C3AED',background:'#F5F3FF',padding:'2px 7px',borderRadius:4}}>{b.category}</span>}
                            {b.language&&b.language!=='English'&&<span style={{fontSize:10,color:'#0891B2',background:'#ECFEFF',padding:'2px 7px',borderRadius:4}}>{b.language}</span>}
                            {b.published_year&&<span style={{fontSize:10,color:'#6B7280',background:'#F3F4F6',padding:'2px 7px',borderRadius:4}}>{b.published_year}</span>}
                          </div>
                          {b.shelf_location&&<div style={{fontSize:11,color:'#9CA3AF',marginBottom:4}}>📍 {b.shelf_location}</div>}
                          {b.isbn&&<div style={{fontSize:11,color:'#9CA3AF',marginBottom:8}}>ISBN: {b.isbn}</div>}
                          <div style={{display:'flex',gap:6}}>
                            <button onClick={()=>{setEditBook(b);setShowBookForm(true);}} style={{flex:1,padding:'6px 0',borderRadius:7,border:'1px solid #E5E7EB',background:'#F9FAFB',color:'#374151',fontSize:11,fontWeight:600,cursor:'pointer'}}>✏️ Edit</button>
                            <button onClick={()=>handleDeleteBook(b.id)} style={{padding:'6px 10px',borderRadius:7,border:'1px solid #FECACA',background:'#FEF2F2',cursor:'pointer'}}>
                              <Trash2 size={12} color="#EF4444"/>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>}
          </>
        )}

        {/* ── ACTIVE BORROWS TAB ── */}
        {(tab==='active'||tab==='history') && (
          <>
            <div style={{background:'#fff',borderRadius:14,border:'1px solid #E5E7EB',padding:12,marginBottom:14,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
              <div style={{position:'relative',flex:1,minWidth:200}}>
                <Search size={14} color="#9CA3AF" style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
                <input value={borInput} onChange={e=>hs(e.target.value,setBorInput,setBorSearch,brTimer)}
                  placeholder="Search by student name, roll no, book title..." style={{...INP,paddingLeft:28}}/>
              </div>
              {tab==='active'&&(
                <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{...INP,width:'auto'}}>
                  <option value="">All Active</option>
                  <option value="borrowed">Borrowed</option>
                  <option value="overdue">Overdue</option>
                </select>
              )}
              <span style={{fontSize:12,color:'#6B7280',fontWeight:600}}>{filtBorrows.filter(b=>tab==='active'?b.status!=='returned':b.status==='returned').length} records</span>
            </div>
            {loading ? <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>Loading...</div> : (
              <div style={{background:'#fff',borderRadius:16,border:'1px solid #E5E7EB',overflow:'auto'}}>
                {filtBorrows.filter(b=>tab==='active'?b.status!=='returned':b.status==='returned').length===0
                  ? <div style={{padding:48,textAlign:'center',color:'#9CA3AF'}}>No records found</div>
                  : <table style={{width:'100%',borderCollapse:'collapse',minWidth:900}}>
                      <thead>
                        <tr style={{background:'#F8FAFF',borderBottom:'1px solid #E5E7EB'}}>
                          {['Book','Student','Roll No','Class','Issued','Due / Returned','Status','Fine','Actions'].map(h=>(
                            <th key={h} style={{padding:'11px 14px',textAlign:'left',fontSize:11,fontWeight:800,color:'#6B7280',whiteSpace:'nowrap'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtBorrows
                          .filter(b=>tab==='active'?b.status!=='returned':b.status==='returned')
                          .map((b,i,arr)=>{
                            const sc = statusColors[b.status]||statusColors.borrowed;
                            const daysOverdue = b.status==='overdue'
                              ? Math.ceil((new Date()-new Date(b.due_date))/(1000*60*60*24)) : 0;
                            return (
                              <tr key={b.id} style={{borderBottom:i<arr.length-1?'1px solid #F1F5F9':'none',background:b.status==='overdue'?'#FFF5F5':b.status==='returned'?'#FAFFF5':'transparent'}}>
                                <td style={{padding:'10px 14px'}}>
                                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                                    {b.book_cover_url
                                      ? <img src={b.book_cover_url} alt="" style={{width:28,height:38,objectFit:'cover',borderRadius:4,flexShrink:0}}/>
                                      : <div style={{width:28,height:38,background:'#EFF6FF',borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><BookOpen size={14} color="#BFDBFE"/></div>}
                                    <div>
                                      <div style={{fontWeight:700,color:'#111827',fontSize:12,maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.book_title}</div>
                                      {b.book_author&&<div style={{fontSize:10,color:'#9CA3AF'}}>{b.book_author}</div>}
                                    </div>
                                  </div>
                                </td>
                                <td style={{padding:'10px 14px'}}>
                                  <div style={{fontWeight:700,color:'#111827',fontSize:12}}>{b.student_name}</div>
                                  {b.student_username&&<div style={{fontSize:10,color:'#9CA3AF'}}>@{b.student_username}</div>}
                                </td>
                                <td style={{padding:'10px 14px'}}>
                                  {b.student_roll_number
                                    ? <code style={{fontSize:11,fontWeight:800,color:'#4F8EF7',background:'#EFF6FF',padding:'2px 6px',borderRadius:4}}>{b.student_roll_number}</code>
                                    : <span style={{color:'#D1D5DB'}}>—</span>}
                                </td>
                                <td style={{padding:'10px 14px',fontSize:11,color:'#6B7280'}}>{b.student_class||'—'}</td>
                                <td style={{padding:'10px 14px',fontSize:11,color:'#374151'}}>
                                  {new Date(b.issued_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                                  <div style={{fontSize:10,color:'#9CA3AF'}}>{new Date(b.issued_at).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})}</div>
                                </td>
                                <td style={{padding:'10px 14px'}}>
                                  {b.status==='returned' && b.returned_at
                                    ? <div>
                                        <div style={{fontSize:11,color:'#059669',fontWeight:700}}>Returned</div>
                                        <div style={{fontSize:10,color:'#9CA3AF'}}>{new Date(b.returned_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</div>
                                      </div>
                                    : <div>
                                        <div style={{fontSize:11,color:b.status==='overdue'?'#EF4444':'#374151',fontWeight:700}}>
                                          {new Date(b.due_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                                        </div>
                                        {b.status==='overdue'&&<div style={{fontSize:10,color:'#EF4444',fontWeight:800}}>{daysOverdue}d overdue!</div>}
                                      </div>}
                                </td>
                                <td style={{padding:'10px 14px'}}>
                                  <span style={{fontSize:10,fontWeight:800,color:sc.color,background:sc.bg,border:`1px solid ${sc.border}`,padding:'3px 8px',borderRadius:5}}>
                                    {b.status.toUpperCase()}
                                  </span>
                                </td>
                                <td style={{padding:'10px 14px'}}>
                                  {b.fine_amount>0
                                    ? <div>
                                        <div style={{fontSize:12,fontWeight:800,color:'#EF4444'}}>Rs {Number(b.fine_amount).toLocaleString()}</div>
                                        {b.fine_paid
                                          ? <div style={{fontSize:10,color:'#10B981',fontWeight:700}}>✅ Paid</div>
                                          : <button onClick={()=>handleMarkFinePaid(b.id)} style={{fontSize:9,color:'#10B981',background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:4,padding:'2px 6px',cursor:'pointer',fontWeight:700}}>Mark Paid</button>}
                                      </div>
                                    : <span style={{fontSize:11,color:'#9CA3AF'}}>—</span>}
                                </td>
                                <td style={{padding:'8px 12px'}}>
                                  {b.status!=='returned' && (
                                    <button onClick={()=>handleReturn(b)}
                                      style={{padding:'6px 12px',borderRadius:7,border:'none',background:'linear-gradient(135deg,#10B981,#059669)',color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                                      ✅ Return
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>}
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {showBookForm && (
          <BookFormModal key={editBook?.id||'new-book'} editBook={editBook}
            institutionId={institution?.id} account={account}
            onSave={onSaved} onClose={()=>{setShowBookForm(false);setEditBook(null);}}/>
        )}
        {showIssue && issueStudent && (
          <IssueBookModal key={issueStudent.id} student={issueStudent}
            books={books} institutionId={institution?.id} account={account}
            onSave={onSaved} onClose={()=>{setShowIssue(false);setIssueStudent(null);}}/>
        )}

      </LMSPageGuard>
    </LMSLayout>
  );
}